import { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "../api/adminApi";
import { createRoulettePrize } from "../utils/defaults";
import {
  prependArrayItem,
  removeArrayItem,
  updateArrayItem
} from "../utils/arrayEditors";

export function useAdminRoulette({ adminToken, enabled }) {
  const [rouletteConfig, setRouletteConfig] = useState(null);
  const [rouletteLoading, setRouletteLoading] = useState(false);
  const [rouletteSaving, setRouletteSaving] = useState(false);
  const [rouletteDirty, setRouletteDirty] = useState(false);
  const [rouletteTab, setRouletteTab] = useState("free");

  const load = useCallback(async (token = adminToken) => {
    if (!token) return;
    setRouletteLoading(true);
    try {
      const res = await adminApi.getRouletteConfig(token);
      setRouletteConfig(res.data || null);
      setRouletteDirty(false);
    } catch (error) {
      console.error("Ошибка загрузки рулетки:", error);
    } finally {
      setRouletteLoading(false);
    }
  }, [adminToken]);

  const save = useCallback(async () => {
    if (!adminToken || !rouletteConfig) return;
    setRouletteSaving(true);
    try {
      await adminApi.saveRouletteConfig(adminToken, rouletteConfig);
      setRouletteDirty(false);
    } catch (error) {
      console.error("Ошибка сохранения рулетки:", error);
      window.alert("Ошибка сохранения рулетки");
    } finally {
      setRouletteSaving(false);
    }
  }, [adminToken, rouletteConfig]);

  useEffect(() => {
    if (!enabled || !adminToken) return;
    load(adminToken);
  }, [enabled, adminToken, load]);

  const mutatePrizes = useCallback((updater) => {
    setRouletteConfig((prev) => {
      if (!prev) return prev;
      const currentBlock = { ...(prev[rouletteTab] || {}) };
      const nextPrizes = updater(Array.isArray(currentBlock.prizes) ? currentBlock.prizes : []);
      return {
        ...prev,
        [rouletteTab]: {
          ...currentBlock,
          prizes: nextPrizes
        }
      };
    });
    setRouletteDirty(true);
  }, [rouletteTab]);

  const prizes = useMemo(() => {
    if (!rouletteConfig || !rouletteConfig[rouletteTab]) return [];
    return Array.isArray(rouletteConfig[rouletteTab].prizes) ? rouletteConfig[rouletteTab].prizes : [];
  }, [rouletteConfig, rouletteTab]);

  return {
    state: {
      rouletteConfig,
      rouletteLoading,
      rouletteSaving,
      rouletteDirty,
      rouletteTab,
      prizes
    },
    actions: {
      setRouletteTab,
      reload: () => load(adminToken),
      save,
      updatePrize: (index, patch) => mutatePrizes((list) => updateArrayItem(list, index, patch)),
      removePrize: (index) => mutatePrizes((list) => removeArrayItem(list, index)),
      addPrize: () => mutatePrizes((list) => prependArrayItem(list, createRoulettePrize()))
    }
  };
}

export default useAdminRoulette;
